import { Button, Dialog, DialogActions, DialogContent, DialogTitle, Stack } from '@mui/material'
import { usePlanningStore } from '@planning/AppProvider'
import { RailcarAutocomplete } from '@planning/components'
import { useTranslate } from '@tolgee/react'
import { observer } from 'mobx-react-lite'
import { FC, useEffect, useState } from 'react'

type Props = {
  open: boolean
  onClose: () => void
  onConfirm: (railcarId: number) => Promise<void>
  title: string
  confirmLabel: string
  railVisitId: number
  excludeCurrentRailcarId?: number
}

export const RailcarAssignmentDialog: FC<Props> = observer(props => {
  const { open, onClose, onConfirm, title, confirmLabel, railVisitId, excludeCurrentRailcarId } =
    props
  const { t } = useTranslate()

  const { railVisitItemStore, railcarItemStore } = usePlanningStore()

  const [selectedRailcarId, setSelectedRailcarId] = useState<number | undefined>()

  useEffect(() => {
    if (open) {
      railcarItemStore.fetch()
    }
  }, [open, railcarItemStore])

  let excludeIds: number[] = []

  const visitItem = railVisitItemStore.getById(railVisitId)

  if (visitItem) {
    const occupiedIds = [
      ...visitItem.discharge.containerOrders,
      ...visitItem.load.containerOrders,
    ].map(o => o.data.railcarId)

    if (excludeCurrentRailcarId) {
      occupiedIds.push(excludeCurrentRailcarId)
    }

    const availableVisitRailcarIds = visitItem.railcarTrackPositions
      .map(pos => pos.data.railcarId)
      .filter((id): id is number => !!id && !occupiedIds.includes(id))

    const allSystemRailcars = railcarItemStore.getRailcars()

    excludeIds = allSystemRailcars
      .map(rc => rc.id)
      .filter(id => !availableVisitRailcarIds.includes(id))
  }

  const handleConfirm = async () => {
    if (selectedRailcarId) {
      await onConfirm(selectedRailcarId)
      onClose()
    }
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth='xs'
      PaperProps={{
        sx: { overflowY: 'visible' },
      }}
    >
      <DialogTitle>{title}</DialogTitle>
      <DialogContent sx={{ overflow: 'visible' }}>
        <Stack spacing={2} pt={1}>
          <RailcarAutocomplete
            label={t('railcarNo', 'Railcar no.')}
            value={selectedRailcarId}
            onChange={setSelectedRailcarId}
            hasError={false}
            excludeIds={excludeIds}
            required
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button
          onClick={handleConfirm}
          variant='contained'
          color='primary'
          disabled={!selectedRailcarId}
        >
          {confirmLabel}
        </Button>
        <Button onClick={onClose} color='inherit'>
          {t('cancel', 'Cancel')}
        </Button>
      </DialogActions>
    </Dialog>
  )
})
